import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { MethodChip, Notice } from "../../../design/components";
import type { MethodId } from "../../../design/methods";
import { ApiError } from "../../../api/client";
import type { ConnectionCard, ConnectionMethod, Entity } from "../../../api/types";
import { patchEntity, saveConnection } from "../../hub/onboard/onboardApi";
import { METHOD_DEFAULTS, MethodForm } from "../../hub/onboard/steps/MethodForm";
import styles from "./connection.module.css";

const KEPT_BUT_UNUSED =
  "The settings saved for the current method are kept but no longer used once another method is chosen.";

const CHOICES: Array<{ method: ConnectionMethod; id: MethodId }> = [
  { method: "direct_api", id: 1 },
  { method: "agent", id: 2 },
  { method: "file", id: 3 },
  { method: "push", id: 4 },
];

function seedFrom(config: Record<string, unknown>): Record<string, string> {
  const seed: Record<string, string> = {};
  for (const [key, value] of Object.entries(config)) seed[key] = value == null ? "" : String(value);
  return seed;
}

export function MethodSettings({
  entity,
  card,
  canEdit,
}: {
  entity: Entity;
  card: ConnectionCard;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [changingMethod, setChangingMethod] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [method, setMethod] = useState<ConnectionMethod>(card.method);
  const [config, setConfig] = useState<Record<string, string>>(() => seedFrom(card.config));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // A background refetch must not overwrite what is being typed.
  useEffect(() => {
    if (editing) return;
    setMethod(card.method);
    setConfig(seedFrom(card.config));
  }, [card, editing]);

  function patch(next: Record<string, string>): void {
    setConfig((current) => ({ ...current, ...next }));
  }

  function chooseMethod(next: ConnectionMethod): void {
    setMethod(next);
    setConfig((current) => ({ ...METHOD_DEFAULTS[next], ...current }));
  }

  function cancel(): void {
    setEditing(false);
    setChangingMethod(false);
    setError("");
    setMethod(card.method);
    setConfig(seedFrom(card.config));
  }

  async function save(): Promise<void> {
    setSaving(true);
    setError("");
    try {
      // A push company has no ERP settings to send: the hub holds only its limits (server defaults).
      const body =
        method === "push"
          ? {}
          : Object.fromEntries(Object.entries(config).filter(([, value]) => value !== ""));
      if (method === "file") body.drop_folder = `/drop/${entity.code}/in`;
      await saveConnection(entity.id, method, body);
      if (method !== card.method) await patchEntity(entity.id, { connectionMethod: method });
      await queryClient.invalidateQueries({ queryKey: ["entities", entity.id, "connection"] });
      await queryClient.invalidateQueries({ queryKey: ["entities", entity.id] });
      setEditing(false);
      setChangingMethod(false);
    } catch (exc) {
      setError(
        exc instanceof ApiError ? exc.message : t("companies.methodSettings.saveError", "The connection could not be saved."),
      );
    } finally {
      setSaving(false);
    }
  }

  const value = (key: string, fallback = "") => config[key] ?? fallback;

  return (
    <section className="card">
      <div className="card-head wrap">
        <h2 className="grow">{t("companies.methodSettings.heading", "Connection settings")}</h2>
        {editing ? (
          <>
            <button type="button" className="btn btn-sm" onClick={cancel}>
              {t("companies.methodSettings.cancelButton", "Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={saving}
              onClick={() => void save()}
            >
              {t("companies.methodSettings.saveButton", "Save settings")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-sm"
              disabled={!canEdit}
              onClick={() => setConfirming(true)}
            >
              {t("companies.methodSettings.changeMethodButton", "Change method")}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!canEdit}
              onClick={() => setEditing(true)}
            >
              {t("companies.methodSettings.editButton", "Edit settings")}
            </button>
          </>
        )}
      </div>

      <div className="card-body">
        {changingMethod ? (
          <div
            className={styles.methods}
            role="radiogroup"
            aria-label={t("companies.methodSettings.methodRadiogroupLabel", "Connection method")}
          >
            {CHOICES.map((choice) => (
              <button
                key={choice.method}
                type="button"
                role="radio"
                aria-checked={method === choice.method}
                className={styles.methodCard}
                onClick={() => chooseMethod(choice.method)}
              >
                <MethodChip method={choice.id} full />
              </button>
            ))}
          </div>
        ) : null}

        <MethodForm
          method={method}
          prefix="conn"
          code={entity.code}
          value={value}
          setConfig={patch}
          disabled={!editing}
          families={entity.enabledDocTypes}
        />

        {error ? <Notice kind="fail">{error}</Notice> : null}
      </div>

      {confirming ? (
        <div
          className={styles.dialog}
          role="dialog"
          aria-label={t("companies.methodSettings.changeMethodDialogLabel", "Change the connection method")}
        >
          <p>{t("companies.methodSettings.notice", KEPT_BUT_UNUSED)}</p>
          <div className={styles.dialogFoot}>
            <button type="button" className="btn" onClick={() => setConfirming(false)}>
              {t("companies.methodSettings.cancelButton", "Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setConfirming(false);
                setChangingMethod(true);
                setEditing(true);
              }}
            >
              {t("companies.methodSettings.continueButton", "Continue")}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
